import { ApolloClient } from "@apollo/client";

import { APPLY_DISCOUNT_CODE } from "../graphql/resolvers/order";
import {
  GET_MEMBER_LAYOUT_INFO,
  GET_MEMBER_SHOPPING_CART,
  GET_MEMBER_SUBSCRIPTION,
} from "../graphql/resolvers/member";
import {
  ADD_SHOPPING_CART_ITEM,
  DELETE_PRODUCT_TYPE_SHOPPING_CART_ITEMS,
  TOGGLE_SHOPPING_CART_SUBSCRIPTION,
} from "../graphql/resolvers/cart";
import { AddPromoCodeInfo, AddPromoCodeInfoVariables } from "../types/__generated__/AddPromoCodeInfo";
import {
  AddShoppingCartItem,
  AddShoppingCartItemVariables,
} from "../types/__generated__/AddShoppingCartItem";
import { MemberShoppingCart } from "../types/__generated__/MemberShoppingCart";
import {
  DeleteProductTypeShoppingCartItems,
  DeleteProductTypeShoppingCartItemsVariables,
} from "../types/__generated__/DeleteProductTypeShoppingCartItems";
import {
  ToggleShoppingCartSubscription,
  ToggleShoppingCartSubscriptionVariables,
} from "../types/__generated__/ToggleShoppingCartSubscription";
import { PRODUCT_TYPE_IDS } from "./constants";
import { sanitiseExtraInfoInput } from "./extraInfo";
import { logError } from "./logging";
import { getOtherCartLines, markBuildYourOwnApplied } from "./buildYourOwn";

export type ApplyBuildYourOwnOutcome =
  | { ok: true }
  | { ok: false; message: string }
  /** The cart holds items a wines-only order cannot keep, named here; nothing was changed. */
  | { ok: false; blockedBy: string[] };

export interface ApplyBuildYourOwnOptions {
  /** Drop the lines `blockedBy` would otherwise name, before applying. */
  removeOtherItems?: boolean;
  /** How many wines the calendar takes, kept with the applied marker for the add guard. */
  perNumberOfWines?: number | null;
}

type ApplyClient = Pick<ApolloClient<object>, "mutate" | "query">;

interface RemovedLine {
  productId: number;
  quantity: number;
  extraInfo?: unknown;
}

/** What `ensureOneOffCart` changed, so a refused code can put the cart back. */
interface CartChanges {
  toggledToOneOff: boolean;
  removedLines: RemovedLine[];
}

interface CartLineLike {
  quantity: number;
  extraInfo?: unknown;
  product: { id: number | null; name?: string | null; productType: { id: number | null } | null };
}

const toRemovedLines = (items: CartLineLike[]): RemovedLine[] =>
  items.flatMap((item) =>
    typeof item.product.id === "number"
      ? [{ productId: item.product.id, quantity: item.quantity, extraInfo: item.extraInfo }]
      : []
  );

const productTypeIdsOf = (items: CartLineLike[]): number[] =>
  Array.from(
    new Set(
      items.flatMap((item) => (item.product.productType?.id != null ? [item.product.productType.id] : []))
    )
  );

const FALLBACK_MESSAGE = "Something went wrong. Please try again.";
const CART_READ_FAILED_MESSAGE = "We couldn't read your cart. Please try again.";

const cartRefetches = [
  { query: GET_MEMBER_SHOPPING_CART },
  { query: GET_MEMBER_SUBSCRIPTION },
  { query: GET_MEMBER_LAYOUT_INFO, variables: { skipMe: false } },
];

const toggleSubscriptionMode = (client: ApplyClient, memberId: number) =>
  client.mutate<ToggleShoppingCartSubscription, ToggleShoppingCartSubscriptionVariables>({
    mutation: TOGGLE_SHOPPING_CART_SUBSCRIPTION,
    variables: { input: { memberId } },
    refetchQueries: cartRefetches,
    awaitRefetchQueries: true,
  });

const deleteProductType = (client: ApplyClient, memberId: number, productTypeId: number) =>
  client.mutate<DeleteProductTypeShoppingCartItems, DeleteProductTypeShoppingCartItemsVariables>({
    mutation: DELETE_PRODUCT_TYPE_SHOPPING_CART_ITEMS,
    variables: { input: { memberId, productTypeId } },
    refetchQueries: cartRefetches,
    awaitRefetchQueries: true,
  });

/**
 * A build-your-own calendar is a one-off, wines-only purchase. A subscriber's
 * cart may be on "monthly box" (every add then goes to the next box) and may
 * hold the Subscription line that pays for that box, which the backend refuses
 * the code for ("cannot be used for subscriptions"). Switch to one-off first,
 * as adding a special pack does: flip the toggle and drop the Subscription
 * line. Same mutations and refetches as the cart tray, so the header and tray
 * follow. Anything else that isn't wine is the shopper's to give up: it is
 * named in `blockedBy` (and nothing is touched) until `removeOtherItems` asks
 * for those lines to go too.
 *
 * Reports each step as it lands — even when a later step fails — so
 * `restoreCart` can undo exactly what was done.
 */
const ensureOneOffCart = async (
  client: ApplyClient,
  memberId: number,
  removeOtherItems: boolean
): Promise<{ changes: CartChanges; error?: Error; blockedBy?: string[] }> => {
  const changes: CartChanges = { toggledToOneOff: false, removedLines: [] };
  let isMonthlyBox = false;
  let subscriptionLines: CartLineLike[] = [];
  let otherLines: CartLineLike[] = [];
  /* The read decides what has to change first, so it has to have worked: the
   * client's "all" error policy can resolve with errors and no data, which must
   * not pass for an empty one-off cart. (A member with no cart yet reads as
   * data with a null cart — that one really is empty.) */
  try {
    const result = await client.query<MemberShoppingCart>({
      query: GET_MEMBER_SHOPPING_CART,
      fetchPolicy: "network-only",
    });
    if (!result?.data || result.errors?.length) throw new Error(result?.errors?.[0]?.message || "no data");
    const cart = result.data.me?.shoppingCart;
    const items: CartLineLike[] = cart?.shoppingcartitemSet ?? [];
    isMonthlyBox = !!cart?.shouldWinesBeAddedToSubscription;
    subscriptionLines = items.filter(
      (item) => item.product.productType?.id === PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION
    );
    otherLines = getOtherCartLines(items);
  } catch (error) {
    logError(error as Error, { location: "applyBuildYourOwnPromoCode", action: "readCart" });
    return { changes, error: new Error(CART_READ_FAILED_MESSAGE) };
  }

  if (otherLines.length > 0 && !removeOtherItems) {
    return { changes, blockedBy: otherLines.map((item) => item.product.name ?? "").filter(Boolean) };
  }

  try {
    if (isMonthlyBox) {
      await toggleSubscriptionMode(client, memberId);
      changes.toggledToOneOff = true;
    }
    if (subscriptionLines.length > 0) {
      await deleteProductType(client, memberId, PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION);
      changes.removedLines.push(...toRemovedLines(subscriptionLines));
    }
    await productTypeIdsOf(otherLines).reduce<Promise<void>>(async (previous, productTypeId) => {
      await previous;
      await deleteProductType(client, memberId, productTypeId);
      changes.removedLines.push(
        ...toRemovedLines(otherLines.filter((item) => item.product.productType?.id === productTypeId))
      );
    }, Promise.resolve());
  } catch (error) {
    return { changes, error: error as Error };
  }
  return { changes };
};

/**
 * Puts a cart back the way `ensureOneOffCart` found it when the code wasn't
 * applied after all: "monthly box" is switched back on and the Subscription
 * line re-added. Best effort — a failure here is logged, and the caller still
 * reports why the code was refused.
 */
const restoreCart = async (client: ApplyClient, memberId: number, changes: CartChanges) => {
  if (!changes.toggledToOneOff && changes.removedLines.length === 0) return;
  try {
    if (changes.toggledToOneOff) {
      await toggleSubscriptionMode(client, memberId);
    }
    await Promise.all(
      changes.removedLines.map(({ productId, quantity, extraInfo }) =>
        client.mutate<AddShoppingCartItem, AddShoppingCartItemVariables>({
          mutation: ADD_SHOPPING_CART_ITEM,
          variables: {
            input: { memberId, productId, quantity, extraInfo: sanitiseExtraInfoInput(extraInfo) },
          },
          refetchQueries: cartRefetches,
          awaitRefetchQueries: true,
        })
      )
    );
  } catch (error) {
    logError(error as Error, { location: "applyBuildYourOwnPromoCode", action: "restoreCart" });
  }
};

/**
 * Applies the edition's build-your-own promo code to the member's cart, after
 * switching a "monthly box" cart to one-off. Works on an empty cart. The
 * backend's `message` is shown verbatim on error — it names the reason (a
 * mixed cart, an expired code) — and the cart switch is undone so a subscriber
 * isn't left on a one-off order they never asked for. The cart is refetched
 * before resolving so the shop banner is right on arrival, and the applied
 * marker is only set once that fresh cart is in the cache.
 */
export const applyBuildYourOwnPromoCode = async (
  client: ApplyClient,
  memberId: number,
  promoCode: string,
  { removeOtherItems = false, perNumberOfWines = null }: ApplyBuildYourOwnOptions = {}
): Promise<ApplyBuildYourOwnOutcome> => {
  const {
    changes,
    error: switchError,
    blockedBy,
  } = await ensureOneOffCart(client, memberId, removeOtherItems);
  if (blockedBy) return { ok: false, blockedBy };
  if (switchError) {
    await restoreCart(client, memberId, changes);
    return { ok: false, message: switchError.message || FALLBACK_MESSAGE };
  }

  let payload: AddPromoCodeInfo["addPromoCodeInfo"] | undefined;
  try {
    const result = await client.mutate<AddPromoCodeInfo, AddPromoCodeInfoVariables>({
      mutation: APPLY_DISCOUNT_CODE,
      variables: { input: { memberId, name: promoCode } },
      refetchQueries: [{ query: GET_MEMBER_SHOPPING_CART }],
      awaitRefetchQueries: true,
    });
    payload = result?.data?.addPromoCodeInfo;
  } catch (error) {
    await restoreCart(client, memberId, changes);
    return { ok: false, message: (error as Error)?.message || FALLBACK_MESSAGE };
  }

  if (payload?.status === "success") {
    markBuildYourOwnApplied(promoCode, perNumberOfWines);
    return { ok: true };
  }
  await restoreCart(client, memberId, changes);
  const message = payload?.message || payload?.errors?.[0]?.messages?.[0] || FALLBACK_MESSAGE;
  return { ok: false, message };
};
